// Pure maths for the profile photo's square crop (the /account profile card):
// where the crop sits in the photo, how far it may zoom, panning and zooming
// from the viewport, and the size uploaded. The server centre-crops and
// resizes whatever it gets anyway; this just lets people choose the square.

export interface Size {
  width: number;
  height: number;
}

/**
 * The square cut from the photo: its centre in photo pixels and the zoom,
 * where 1 is the largest square that fits (its side is the shorter side).
 */
export interface Crop {
  x: number;
  y: number;
  zoom: number;
}

/** The server refuses photos whose shorter side is under this (400 `avatar_too_small`). */
export const AVATAR_MIN_PX = 128;
/** The side uploaded at most; the server keeps 512 px. */
export const AVATAR_UPLOAD_PX = 1024;
/** Zooming further than this only shows blur, even on a big photo. */
export const AVATAR_MAX_ZOOM = 8;
/** The server takes at most 10 MB; the crop itself is far smaller, but a picked file is decoded whole. */
export const AVATAR_FILE_MAX_BYTES = 50 * 1024 * 1024;

/** Whether the photo is too small for the server to take. */
export function tooSmall(size: Size): boolean {
  return Math.min(size.width, size.height) < AVATAR_MIN_PX;
}

/** How far the crop may zoom: never so far that the square is under `AVATAR_MIN_PX` photo pixels. */
export function maxZoom(size: Size): number {
  return Math.max(1, Math.min(AVATAR_MAX_ZOOM, Math.min(size.width, size.height) / AVATAR_MIN_PX));
}

/** The crop square's side in photo pixels. */
export function cropSide(size: Size, zoom: number): number {
  return Math.min(size.width, size.height) / zoom;
}

/** Zoom within bounds and the square kept inside the photo. */
export function clampCrop(size: Size, crop: Crop): Crop {
  const zoom = Math.min(maxZoom(size), Math.max(1, Number.isFinite(crop.zoom) ? crop.zoom : 1));
  const half = cropSide(size, zoom) / 2;
  const clamp = (value: number, length: number) =>
    Math.min(length - half, Math.max(half, Number.isFinite(value) ? value : length / 2));
  return { x: clamp(crop.x, size.width), y: clamp(crop.y, size.height), zoom };
}

/** The largest centred square. */
export function initialCrop(size: Size): Crop {
  return { x: size.width / 2, y: size.height / 2, zoom: 1 };
}

/**
 * Drags the photo by (dx, dy) CSS pixels in a viewport `viewPx` wide: the
 * photo follows the pointer, so the crop moves the other way.
 */
export function panCrop(size: Size, crop: Crop, dx: number, dy: number, viewPx: number): Crop {
  const scale = cropSide(size, crop.zoom) / viewPx;
  return clampCrop(size, { ...crop, x: crop.x - dx * scale, y: crop.y - dy * scale });
}

/**
 * Zooms to `zoom`, keeping the photo point under `anchor` (a fraction of the
 * viewport, 0–1 each way; default its centre) where it is.
 */
export function zoomCrop(size: Size, crop: Crop, zoom: number, anchor = { x: 0.5, y: 0.5 }): Crop {
  const before = cropSide(size, crop.zoom);
  const target = clampCrop(size, { ...crop, zoom }).zoom;
  const after = cropSide(size, target);
  const px = crop.x - before / 2 + anchor.x * before;
  const py = crop.y - before / 2 + anchor.y * before;
  return clampCrop(size, { x: px - anchor.x * after + after / 2, y: py - anchor.y * after + after / 2, zoom: target });
}

/** The crop as a source rectangle for `drawImage` (photo pixels). */
export function cropRect(size: Size, crop: Crop): { sx: number; sy: number; side: number } {
  const clamped = clampCrop(size, crop);
  const side = cropSide(size, clamped.zoom);
  return { sx: clamped.x - side / 2, sy: clamped.y - side / 2, side };
}

/** The uploaded square's side: the crop's own pixels, at most `AVATAR_UPLOAD_PX` (never upscaled). */
export function outputSide(size: Size, crop: Crop): number {
  return Math.max(1, Math.min(AVATAR_UPLOAD_PX, Math.floor(cropRect(size, crop).side)));
}

/** The text for a failed photo change, by the problem's status and code. */
export function avatarErrorText(status: number, code: string | undefined): string | null {
  switch (code) {
    case "media_disabled":
      return "Profile photos aren't available right now.";
    case "avatar_too_small":
      return `This photo is too small. Choose one at least ${AVATAR_MIN_PX} pixels across.`;
    case "unsupported_media":
      return "This photo's format isn't supported. Try a JPEG or PNG.";
    case "media_too_large":
      return "This photo is too large.";
  }
  if (status === 429) return "You've changed your photo too often. Try again in an hour.";
  return null;
}
